import
{
    v4 as uuidv4
}
from 'uuid';

import Config from "../../Config";

/**
 * Storing whole metamodel fetched from ADONIS NP and useful getters
 */
export default class MetamodelStore
{
    static myInstance = null;

    /**
     * MetamodelStore is a singleton class
     */
    static getInstance()
    {
        if (MetamodelStore.myInstance == null)
        {
            MetamodelStore.myInstance = new MetamodelStore();
        }
        return MetamodelStore.myInstance;
    }

    /**
     * Private class member storing whole metamodel
     */
    _aMetamodel = null;

    /**
     * Private class member storing icons
     */
    _aMetamodelIcons = null;

    /**
     * Set the metamodel data to class member and create super types
     * @param {Object} aMetamodelInput 
     */
    setMetamodel = (aMetamodelInput) =>
    {
        this._aMetamodel = aMetamodelInput;
        this.createSuperModeltype();
        this.createSuperObject();
    }

    /**
     * Set single icon to class member
     * @param {String} sID 
     * @param {String} sData64 
     */
    setIcon = (sID, sData64) =>
    {
        if (!this._aMetamodelIcons)
        {
            this._aMetamodelIcons = {};
        }
        this._aMetamodelIcons[sID] = sData64;
    }

    /**
     * Get single icon
     * @param {String} sID 
     */
    getIcon = (sID) =>
    {
        if (!this._aMetamodelIcons)
        {
            throw new Error("Icons not loaded from metamodel");
        }
        if (!Object.hasOwnProperty.call(this._aMetamodelIcons, sID))
        {
            return null;
        }
        return this._aMetamodelIcons[sID];
    }

    /**
     * Get whole metamodel
     */
    getMetamodel = () =>
    {
        return this._aMetamodel;
    }

    /**
     * Get all model types and their metadata
     * @param {Boolean} bPreventSorted 
     * @returns 
     */
    getMetaModelTypes = (bPreventSorted) =>
    {
        if (!this._aMetamodel || !Object.hasOwnProperty.call(this._aMetamodel, 'modeltypes'))
        {
            throw new Error("Metamodel is not correct, incomplite or not loaded.");
        }

        if (!bPreventSorted)
        {
            var aItems = MetamodelStore.sortObjectByLangName(this._aMetamodel.modeltypes);
            return aItems;
        }
        return this._aMetamodel.modeltypes;
    }

    /**
     * Sort object items by display name
     * @param {Object} aItems 
     * @returns 
     */
    static sortObjectByLangName = (aItems) =>
    {
        var aItemsArr = Object.entries(aItems);
        aItemsArr.sort((aA, aB) =>
        {
            var aAName = Config.getLangName(aA[1]);
            var aBName = Config.getLangName(aB[1]);
            if (Object.hasOwnProperty.call(aA[1], 'isSuper') && aA[1].isSuper)
            {
                return 1;
            }
            else if (Object.hasOwnProperty.call(aB[1], 'isSuper') && aB[1].isSuper)
            {
                return -1;
            }
            if (aAName > aBName)
            {
                return 1;
            }
            else if (aAName < aBName)
            {
                return -1;
            }
            return 0;
        });
        var aItemsObj = Object.fromEntries(aItemsArr);
        return aItemsObj;
    }

    /**
     * Returns array of object by array of IDs
     * @param {Object} aItems 
     * @returns 
     */
    static getObjectsByID = (aItems, bPreventSorted) =>
    {
        if (!aItems || !Array.isArray(aItems))
        {
            return [];
        }

        var aItemsData = aItems.map(aItem =>
        {
            try
            {
                var aItemData = MetamodelStore.getInstance().getMetaObjectType(aItem);
                return aItemData;
            }
            catch (err)
            {
                console.error(err);
                return null;
            }
        });
        aItemsData.sort((aA, aB) =>
        {
            if (!aA && !aB)
            {
                return 0;
            }
            else if (aA && !aB)
            {
                return -1;
            }
            else if (!aA && aB)
            {
                return 1;
            }
            var aAName = Config.getLangName(aA);
            var aBName = Config.getLangName(aB);
            if (aAName > aBName)
            {
                return 1;
            }
            else if (aAName < aBName)
            {
                return -1;
            }
            return 0;
        });

        return aItemsData;
    }

    /**
     * Get all object types and their metadata
     */
    getMetaObjectTypes = () =>
    {
        if (!this._aMetamodel || !Object.hasOwnProperty.call(this._aMetamodel, 'classes'))
        {
            throw new Error("Metamodel is not correct, incomplite or not loaded.");
        }
        return this._aMetamodel.classes;
    }

    /**
     * Get object types with property 'bVisible' set to true and their metadata
     * @param {Boolean} bPreventSorted 
     * @returns 
     */
    getMetaObjectTypesVisible = (bPreventSorted, bRepoObjectsOnly) =>
    {
        if (!this._aMetamodel || !Object.hasOwnProperty.call(this._aMetamodel, 'classes'))
        {
            throw new Error("Metamodel is not correct, incomplite or not loaded.");
        }

        var aClassesToRet = {};
        for (const key in this._aMetamodel.classes)
        {
            if (this._aMetamodel.classes[key].bVisible)
            {
                if (!bRepoObjectsOnly || (bRepoObjectsOnly && this._aMetamodel.classes[key].bRepositoryClass))
                {
                    aClassesToRet[key] = this._aMetamodel.classes[key];
                }
            }
        }

        if (!bPreventSorted)
        {
            var aItems = MetamodelStore.sortObjectByLangName(aClassesToRet);
            return aItems;
        }

        return aClassesToRet;
    }

    /**
     * Get all attributes and their data
     */
    getMetaAttributes = () =>
    {
        if (!this._aMetamodel || !Object.hasOwnProperty.call(this._aMetamodel, 'attributes'))
        {
            throw new Error("Metamodel is not correct, incomplite or not loaded.");
        }
        return this._aMetamodel.attributes;
    }

    getMetaAttrTypes = () =>
    {
        if (!this._aMetamodel || !Object.hasOwnProperty.call(this._aMetamodel, 'attrtypes'))
        {
            throw new Error("Metamodel is not correct, incomplite or not loaded.");
        }
        return this._aMetamodel.attrtypes;
    }

    getMetaAttributeByLangIndependent = (sMetaName) =>
    {
        try
        {
            const aMetaAttributes = this.getMetaAttributes();
            const aAttrData = Object.values(aMetaAttributes).find((aFindItem) =>
            {
                return aFindItem?.sName === sMetaName
            });
            try
            {
                const aAttrType = this.getMetaAttrTypes()[aAttrData?.type]
                aAttrData.attrtype = aAttrType
            }
            catch (aEx)
            {}
            return aAttrData
        }
        catch (aEx)
        {
            return undefined
        }
    }

    /**
     * Get model type with its data by ID
     * @param {String} sID 
     */
    getMetaModelType = (sID) =>
    {
        if (!this._aMetamodel || !Object.hasOwnProperty.call(this._aMetamodel, 'modeltypes') || !Object.hasOwnProperty.call(this._aMetamodel.modeltypes, sID))
        {
            throw new Error("Metamodel is not correct, incomplite or not loaded or does not contain demanded ID");
        }
        if (!typeof sID === 'string')
        {
            throw new Error("Provided ID is not a string");
        }
        if ('' === sID || '{' != sID.charAt(0) || '}' != sID.charAt(sID.length - 1))
        {
            throw new Error('Provided ID is not correct');
        }
        return this._aMetamodel.modeltypes[sID];
    }

    /**
     * Get object type with its data by ID
     * @param {String} sID 
     */
    getMetaObjectType = (sID) =>
    {
        if (!this._aMetamodel || !Object.hasOwnProperty.call(this._aMetamodel, 'classes') || !Object.hasOwnProperty.call(this._aMetamodel.classes, sID))
        {
            throw new Error("Metamodel is not correct, incomplite or not loaded or does not contain demanded ID");
        }
        if (!typeof sID === 'string')
        {
            throw new Error("Provided ID is not a string");
        }
        if ('' === sID || '{' != sID.charAt(0) || '}' != sID.charAt(sID.length - 1))
        {
            throw new Error('Provided ID is not correct');
        }
        return this._aMetamodel.classes[sID];
    }

    /**
     * Get object type with its data by language independent name
     * @param {String} sName 
     */
    getMetaObjectTypeByLangIndependent = (sName) =>
    {
        if (!this._aMetamodel || !Object.hasOwnProperty.call(this._aMetamodel, 'classes'))
        {
            throw new Error("Metamodel is not correct, incomplite or not loaded or does not contain demanded name");
        }
        if (!typeof sName === 'string')
        {
            throw new Error("Provided language independent name is not a string");
        }
        if ('' === sName)
        {
            throw new Error('Provided language independent name is not correct');
        }
        return Object.values(this._aMetamodel.classes).find(aItem => aItem.sName === sName);
    }

    /**
     * Get property (attribute/relation) and its data using its language independent name
     * @param {String} sName 
     */
    getMetaPropertyByLangIndependent = (sName) =>
    {
        if (!this._aMetamodel || !Object.hasOwnProperty.call(this._aMetamodel, 'attributes') || !Object.hasOwnProperty.call(this._aMetamodel, 'relations'))
        {
            throw new Error("Metamodel is not correct, incomplite or not loaded or does not contain demanded name");
        }
        if (typeof sName != 'string')
        {
            throw new Error("Provided language independent name is not a string");
        }
        if ('' === sName)
        {
            throw new Error('Provided language independent name is not correct');
        }

        var aResult = Object.values(this._aMetamodel.attributes).find(aItem => aItem.sName === sName);
        if (undefined != aResult)
        {
            return aResult;
        }
        aResult = Object.values(this._aMetamodel.relations).find(aItem => aItem.sName === sName);
        if (undefined != aResult)
        {
            return aResult;
        }
        return null;
    }

    /**
     * Create and add super model type to metamodel class property
     * It can be selected as 'All model types' in dropdown list and can allow to access data of all model types
     */
    createSuperModeltype = () =>
    {
        var sID = '';
        sID = sID.concat('{', uuidv4(), '}');
        this._aMetamodel['modeltypes'][sID] = {
            isSuper: true,
            id: sID,
            aDisplayName:
            {
                [Config.lang.selected]: Config.strings.dropdowns.modelAllName
            }
        }
        // console.log('Created super model type with ID: ' + sID);
    }

    /**
     * Create and add super object type to metamodel class property
     * It can be selected as 'All objects' in dropdown list and can allow to access data of all model types
     */
    createSuperObject = () =>
    {
        var sID = '';
        sID = sID.concat('{', uuidv4(), '}');
        this._aMetamodel['classes'][sID] = {
            isSuper: true,
            bVisible: true,
            bRepositoryClass: true,
            id: sID,
            aDisplayName:
            {
                [Config.lang.selected]: Config.strings.dropdowns.objectAllName
            }
        }
        // console.log('Created super object with ID: ' + sID);
    }

    getEndpoints = () =>
    {
        if (!this._aMetamodel || !this._aMetamodel.hasOwnProperty('endpoints'))
        {
            throw new Error("Metamodel is not correct, incomplite or not loaded.");
        }
        return this._aMetamodel.endpoints;
    }

    getRelations = () =>
    {
        if (!this._aMetamodel || !this._aMetamodel.hasOwnProperty('relations'))
        {
            throw new Error("Metamodel is not correct, incomplite or not loaded.");
        }
        return this._aMetamodel.relations;
    }

    getEntity = (sId) =>
    {
        let aEntityFetched;
        try
        {
            aEntityFetched = this.getMetaModelType(sId);
            aEntityFetched["isModeltype"] = true;
            return aEntityFetched;
        }
        catch (aEx)
        {
            try
            {
                aEntityFetched = this.getMetaObjectType(sId);
                aEntityFetched["isClass"] = true;
                return aEntityFetched;
            }
            catch (aEx)
            {
                return null
            }
        }
    }

    getClassAncestors = (sId) =>
    {
        let aClassObj = this.getEntity(sId);
        let aAncestors = [];
        if (aClassObj)
        {
            const getParent = (aChild) =>
            {
                if (aChild.hasOwnProperty("superClass") && aChild.superClass)
                {
                    let aParent = this.getEntity(aChild.superClass);
                    aAncestors.push(aParent);
                    getParent(aParent);
                }
            }
            getParent(aClassObj);
        }
        return aAncestors;
    }

    getClassDescendants = (sId) =>
    {
        let aClassObj = this.getEntity(sId);
        let aDescendants = [];
        if (aClassObj)
        {
            const getChildren = (aParent) =>
            {
                if (aParent.hasOwnProperty("children") && aParent.children)
                {
                    for (let i = 0; i < aParent.children.length; i++)
                    {
                        let aChild = this.getEntity(aParent.children[i]);
                        aDescendants.push(aChild);
                        getChildren(aChild);
                    }
                }
            }
            getChildren(aClassObj);
        }
        return aDescendants;
    }

    getEntityEpRel = (sId, bPreventSorted, bInterrefOnly) =>
    {

        let aEntity = this.getEntity(sId);

        let aEntityRelations = [];

        const aEntityEps = Object.values(this.getEndpoints()).filter((aEp) =>
        {
            if (aEp.targets.includes(aEntity.id))
            {
                return true;
            }
            let aEntityAncestors = this.getClassAncestors(aEntity.id);
            for (let i = 0; i < aEntityAncestors.length; i++)
            {
                if (aEp.targets.includes(aEntityAncestors[i].id))
                {
                    return true;
                    break;
                }
            }
            return false;
        });

        for (let i = 0; i < aEntityEps.length; i++)
        {
            let aEntityRelation = {
                endpoint: aEntityEps[i]
            };
            let aRelation = Object.values(this.getRelations()).find((aRel) =>
            {
                if (aRel.bAbstract || !aRel.bVisible || (bInterrefOnly && !aRel.bInterref))
                {
                    return false;
                }
                let aSecondSideTargetsVisible;
                if (aRel.aToEndpoint === aEntityEps[i].id)
                {
                    aSecondSideTargetsVisible = this.getEpVisibleTargets(aRel.aFromEndpoint);
                }
                if (aRel.aFromEndpoint === aEntityEps[i].id)
                {
                    aSecondSideTargetsVisible = this.getEpVisibleTargets(aRel.aToEndpoint);
                }
                if (!aSecondSideTargetsVisible || 0 >= aSecondSideTargetsVisible.length)
                {
                    return false;
                }
                return true
            });
            if (aRelation)
            {
                aEntityRelation["relation"] = aRelation;
                aEntityRelations.push(aEntityRelation);
            }
        }

        if (bPreventSorted)
        {
            return aEntityRelations;
        }
        else
        {
            let aEntityRelationsSorted = aEntityRelations.sort((aA, aB) =>
            {
                if (Config.getLangName(aA.relation) > Config.getLangName(aB.relation))
                {
                    return 1;
                }
                else if (Config.getLangName(aA.relation) < Config.getLangName(aB.relation))
                {
                    return -1;
                }
                else
                {
                    return 0;
                }
            });
            return aEntityRelationsSorted;
        }
    }

    getEpVisibleTargets = (sEpId) =>
    {
        const aEndpoint = Object.values(this.getEndpoints()).find(aEp =>
        {
            return aEp.id === sEpId;
        });
        if (!aEndpoint)
        {
            return null;
        }
        let aTargets = [];
        for (let i = 0; i < aEndpoint.targets.length; i++)
        {
            let aTarget0 = this.getEntity(aEndpoint.targets[i]);
            if (!aTarget0)
            {
                break;
            }
            aTargets.push(aTarget0);
            let aTargetsDesc = this.getClassDescendants(aTarget0.id);
            if (aTargetsDesc && 0 < aTargetsDesc.length)
            {
                aTargets = aTargets.concat(aTargetsDesc);
            }
        }
        const aTargetsFiltered = aTargets.filter(aTrg =>
        {
            return (aTrg.bVisible && !aTrg.bAbstract);
        });
        return aTargetsFiltered;
    }
}